import { useCallback } from "react"
import { useMutation } from "urql"

import type { SubmitPriceChangesInput, SubmitPriceChangesMutation } from "@/graphql/codegen/graphql"
import { PriceChangeType, SubmitPriceChangesDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseSubmitPriceChangesReturn {
  submitPriceChanges: (input: SubmitPriceChangesInput) => Promise<string | undefined>
  isSubmitting: boolean
}

export const useSubmitPriceChanges = (): UseSubmitPriceChangesReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<SubmitPriceChangesMutation, { input: SubmitPriceChangesInput }>(
    SubmitPriceChangesDocument
  )

  const submitPriceChanges = useCallback(
    async (input: SubmitPriceChangesInput): Promise<string | undefined> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.priceChange.submitPriceChanges
      const hasError = error || output?.__typename === "SubmitPriceChangesFailure"

      if (hasError) {
        const message = output?.__typename === "SubmitPriceChangesFailure" ? output.error.message : error?.message
        notify({
          type: NotificationType.Error,
          message: message ?? "Failed to submit price changes. Please try again.",
        })
        return undefined
      }

      if (output?.__typename === "SubmitPriceChangesSuccess") {
        if (output?.priceChangeSubmission.changeType !== PriceChangeType.SellingPrice) {
          notify({ type: NotificationType.Success, message: "Price changes submitted" })
        }

        return output.priceChangeSubmission.id
      }

      return undefined
    },
    [executeMutation, notify]
  )

  return { submitPriceChanges, isSubmitting: fetching }
}
